import useSearchParams from '../../../hooks/useSearchParams';
import {
  DASHBOARD_FILTERS,
  MODEL_RUNS_TABLE_FILTERS,
  TEST_RESULTS_TABLE_FILTERS,
  TEST_RUNS_TABLE_FILTERS,
  TREE_NODE,
  TREE_TYPE,
  TREE_VIEW_BY,
} from '../../../constants/urlParams';
import useNavigateWithEnvParam from '../../../hooks/useNavigateWithEnvParam';
import useUrlFilters, { UrlFilters } from '../../../hooks/useUrlFilters';
import { OnSubmitEventVal } from '../../../components';

type FilterPropertyMapping = {
  [path: string]: {
    [filterProperty: string]: string;
  };
};

const FILTER_PROPERTY_MAPPINGS: FilterPropertyMapping = {
  '/report/test-results': {
    tag: 'assetTags',
    owner: 'assetOwners',
    name: 'assetName',
    subscriber: 'assetSubscribers',
    path: 'assetPath',
    is_critical: 'criticalAsset',
    database_name: 'databaseName',
    schema_name: 'schemaName',
    test_tag: 'testTags',
    test_owner: 'testOwners',
  },
  '/report/model-runs': {
    tag: 'asset_metadata.tags',
    owner: 'asset_metadata.owners',
    name: 'name',
    subscriber: 'asset_metadata.subscribers',
    path: 'asset_metadata.path',
    is_critical: 'asset_metadata.is_critical',
    database_name: 'database_name',
    schema_name: 'schema',
  },
  '/report/test-runs': {
    tag: 'asset_metadata.tags',
    owner: 'asset_metadata.owners',
    name: 'metadata.table_name',
    subscriber: 'asset_metadata.subscribers',
    path: 'asset_metadata.path',
    is_critical: 'asset_metadata.is_critical',
    database_name: 'metadata.database_name',
    schema_name: 'metadata.schema_name',
    test_tag: 'metadata.test_tags',
    test_owner: 'metadata.test_owners',
  },
};

const useDashboardFilters = () => {
  const navigate = useNavigateWithEnvParam();
  const [searchParams] = useSearchParams();

  const {
    urlFiltersDebounced: urlFilters,
    clearAllFilters,
    addFilter,
    updateFilter,
    clearFilter,
  } = useUrlFilters({
    urlKey: DASHBOARD_FILTERS,
  });

  const onFilterSubmit = (val: OnSubmitEventVal) => {
    const newValue = (val.value || val.prevValue).filter((v) => v !== 'all');

    if (val.prevValue?.length || val.prevValue?.includes('all')) {
      updateFilter(
        val.property,
        val.operator,
        val.prevValue.filter((v) => v !== 'all'),
        newValue,
      );
    } else {
      addFilter(val.property, val.operator, newValue);
    }
  };

  const mapFiltersToColumnProperties = (
    targetPath: string,
    filters: UrlFilters,
  ): UrlFilters => {
    const pathMapping = FILTER_PROPERTY_MAPPINGS[targetPath];

    if (!pathMapping) {
      return filters;
    }

    return filters.map((filter) => {
      const mappedProperty = pathMapping[filter.property];

      if (mappedProperty) {
        return {
          ...filter,
          property: mappedProperty,
          value: filter.value.map((v) => {
            if (v === 'No owners' || v === 'No tags' || v === 'No subscribers')
              return '';
            return v;
          }),
        };
      }

      return filter;
    });
  };

  const setTreeSearchParams = (queryParams: URLSearchParams) => {
    [TREE_NODE, TREE_TYPE, TREE_VIEW_BY].forEach((param) => {
      if (searchParams.get(param)) {
        queryParams.set(param, searchParams.get(param)!);
      }
    });
  };

  const navigateWithFilters = (path: string, params?: URLSearchParams) => {
    const newParams = new URLSearchParams(params);

    if (urlFilters && urlFilters.length > 0) {
      const mappedFilters = mapFiltersToColumnProperties(path, urlFilters);

      let targetFilterKey = '';
      if (path.includes('test-results')) {
        targetFilterKey = TEST_RESULTS_TABLE_FILTERS;
      } else if (path.includes('model-runs')) {
        targetFilterKey = MODEL_RUNS_TABLE_FILTERS;
      } else if (path.includes('test-runs')) {
        targetFilterKey = TEST_RUNS_TABLE_FILTERS;
      }

      if (targetFilterKey && mappedFilters.length > 0) {
        newParams.set(
          targetFilterKey,
          JSON.stringify([
            ...mappedFilters,
            ...newParams
              .getAll(targetFilterKey)
              .flatMap((val) => JSON.parse(val || '[]')),
          ]),
        );
      }
    }

    setTreeSearchParams(newParams);
    navigate(`${path}?${newParams.toString()}`);
  };

  return {
    urlFilters,
    clearAllFilters,
    clearFilter,
    navigateWithFilters,
    onFilterSubmit,
  };
};

export default useDashboardFilters;
